import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import EducationShell from "../../components/education/EducationShell";

// ─── Palette — matches EducationLanding.jsx ──────────────────────────────────
const INK = "#0F172A";
const SLATE = "#475569";
const MUTED = "#64748B";
const BORDER = "#E2E8F0";
const ACCENT = "#0F766E";
const ACCENT_SOFT = "#F0FDFA";
const PAPER = "#FFFFFF";

const CAUSES = [
  {
    code: "DN",
    path: "/education/types/diabetic",
    name: "Diabetic Nephropathy",
    subtitle: "Diabetic Kidney Disease",
    body: "Caused by long-term high blood sugar damaging the kidneys' filtering units. The single most common cause of CKD and kidney failure in the U.S.",
  },
  {
    code: "HN",
    path: "/education/types/hypertensive",
    name: "Hypertensive Nephrosclerosis",
    subtitle: "Hypertensive Kidney Disease",
    body: "Caused by long-standing high blood pressure damaging the small arteries feeding the kidneys. The second most common cause of kidney failure.",
  },
  {
    code: "GN",
    path: "/education/types/glomerulonephritis",
    name: "Glomerulonephritis",
    subtitle: "Immune-Mediated",
    body: "A group of diseases causing immune-driven inflammation of the kidneys' filtering units — includes IgA nephropathy, FSGS, and lupus nephritis.",
  },
  {
    code: "PKD",
    path: "/education/types/pkd",
    name: "Polycystic Kidney Disease",
    subtitle: "Hereditary",
    body: "A genetic disorder causing fluid-filled cysts to grow in the kidneys, gradually enlarging them and impairing function.",
  },
  {
    code: "Other",
    path: "/education/types/other",
    name: "Other & Unknown Causes",
    subtitle: "Less Common",
    body: "Includes obstructive uropathy, recurrent kidney infections, certain medications, and cases where a clear cause hasn't been identified.",
  },
];

export default function TypesLanding() {
  const navigate = useNavigate();

  return (
    <EducationShell breadcrumbs={[{ label: "Learn", path: "/education" }, { label: "CKD Causes" }]}>
      <Box sx={{ fontFamily: "'DM Sans', sans-serif", bgcolor: PAPER }}>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=Fraunces:ital,wght@0,300;0,600;1,300&display=swap');
        `}</style>

        {/* Intro */}
        <Box sx={{ px: { xs: 3, md: 8 }, pt: { xs: 5, md: 7 }, pb: { xs: 4, md: 5 } }}>
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, letterSpacing: "0.14em", textTransform: "uppercase", color: ACCENT, mb: 1.5 }}>
            CKD Causes
          </Typography>
          <Typography sx={{ fontFamily: "'Fraunces', serif", fontSize: { xs: "1.7rem", md: "2.3rem" }, fontWeight: 600, color: INK, mb: 2, maxWidth: 640 }}>
            CKD has several underlying causes
          </Typography>
          <Typography sx={{ fontSize: "0.95rem", color: SLATE, maxWidth: 620, lineHeight: 1.7 }}>
            The underlying cause of your kidney disease shapes how it's monitored, which complications to watch
            for, and which treatments and clinical trials apply. Select your cause below for detail on features,
            diagnosis, and treatment.
          </Typography>
        </Box>

        {/* Cause cards */}
        <Box sx={{ px: { xs: 3, md: 8 }, pb: { xs: 7, md: 9 } }}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2.5 }}>
            {CAUSES.map((c) => (
              <Box
                key={c.code}
                onClick={() => navigate(c.path)}
                sx={{
                  border: `1px solid ${BORDER}`,
                  borderRadius: "10px",
                  p: { xs: 3, md: 3.5 },
                  cursor: "pointer",
                  transition: "border-color 0.15s ease",
                  "&:hover": { borderColor: ACCENT },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                  <Box
                    sx={{
                      display: "inline-flex",
                      px: 1.5,
                      py: 0.5,
                      borderRadius: "6px",
                      bgcolor: ACCENT_SOFT,
                      color: ACCENT,
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      letterSpacing: "0.04em",
                    }}
                  >
                    {c.code}
                  </Box>
                  <ArrowForwardIcon sx={{ fontSize: 18, color: MUTED }} />
                </Box>
                <Typography sx={{ fontFamily: "'Fraunces', serif", fontSize: "1.25rem", fontWeight: 600, color: INK, mb: 0.25 }}>
                  {c.name}
                </Typography>
                <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: MUTED, mb: 1.5 }}>
                  {c.subtitle}
                </Typography>
                <Typography sx={{ fontSize: "0.87rem", color: SLATE, lineHeight: 1.65 }}>
                  {c.body}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </EducationShell>
  );
}